import React, { createRef } from 'react';

// constants
import {
  ADD_CUSTOMER_TOOLTIP,
  BACK,
} from './constants';

import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';

import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { Button, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneIcon from '@material-ui/icons/Phone';
import { AddAPhoto, Camera } from '@material-ui/icons';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import debounce from 'lodash/debounce';
import './Customer.css';
import { BASE_URL } from '../global/Server/endpoints';
import BlacklistWarningPanel from './BlacklistWarningPanel';
import DuplicateCustomerPanel from './DuplicateCustomerPanel';

const DEFAULT_POINTS = 20;

class CustomerForm extends React.Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.videoRef = createRef();
    this.canvasRef = createRef();
    this.state = {
      firstName: '',
      lastName: '',
      dob: null,
      comments: '',
      phone: '',
      files: [],
      newFiles: [],
      capturedImageURL: null,
      showVideo: false,
      isCameraOpen: false,
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,

      blacklistMatches: [],
      blacklistDismissed: false,

      duplicateNameMatches: [],
      duplicateNameDismissed: false,

      duplicatePhoneMatches: [],
      duplicatePhoneDismissed: false,

      saving: false,
    };
  }

  componentWillUnmount() {
    if (this.videoRef.current && this.videoRef.current.srcObject) {
      const stream = this.videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleDateChange = (date, key) => {
    this.setState({ [key]: date });
  };

  handleNameChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value, duplicateNameDismissed: false }, () => {
      const { firstName, lastName } = this.state;

      if (firstName && lastName) {
        this.debouncedCheckDuplicateNameMatches(firstName, lastName);
      }

      if (name === 'firstName' || name === 'lastName') {
        this.setState({ blacklistDismissed: false });
        this.debouncedCheckBlacklistMatch();
      }
    });
  };

  handlePhoneChange = (event) => {
    const { name, value } = event.target;
    let formattedValue = value.replace(/[^0-9]/g, '');
    if (formattedValue.length > 9) {
      formattedValue = formattedValue.substring(0, 10);
    }
    this.setState(
      {
        [name]: formattedValue,
        duplicatePhoneDismissed: false,
        blacklistDismissed: false,
      },
      () => {
        this.debouncedCheckDuplicatePhone(formattedValue);
        this.debouncedCheckBlacklistMatch();
      }
    );
  };

  checkBlacklistMatch = async () => {
    const firstName = (this.state.firstName || '').trim();
    const lastName = (this.state.lastName || '').trim();
    const phone = (this.state.phone || '').trim();

    const hasSearch =
      firstName.length >= 2 || lastName.length >= 2 || phone.length >= 2;
    if (!hasSearch) {
      this.setState({ blacklistMatches: [] });
      return;
    }

    try {
      const locationId = this.props.user?.selectedLocationId;
      const token = localStorage.getItem('token');

      const body = { locationId };
      if (firstName.length >= 2) body.firstName = firstName;
      if (lastName.length >= 2) body.lastName = lastName;
      if (phone.length >= 2) body.phone = phone;

      const response = await fetch(`${BASE_URL}/customer/blacklistSearch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ blacklistMatches: data.customers || [] });
      }
    } catch (err) {
      // silent
    }
  };

  debouncedCheckBlacklistMatch = debounce(this.checkBlacklistMatch, 400);

  checkDuplicatePhone = (phone) => {
    const trimmed = (phone || '').replace(/\D/g, '');
    if (trimmed.length < 7) {
      this.setState({ duplicatePhoneMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'phone',
          phone: trimmed,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicatePhoneMatches: customers || [],
            duplicatePhoneDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  checkDuplicateName = (firstName, lastName) => {
    if (!firstName?.trim() || !lastName?.trim()) {
      this.setState({ duplicateNameMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'name',
          firstName,
          lastName,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicateNameMatches: customers || [],
            duplicateNameDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  debouncedCheckDuplicatePhone = debounce(this.checkDuplicatePhone, 400);

  debouncedCheckDuplicateNameMatches = debounce(this.checkDuplicateName, 400);

  // ── Camera ────────────────────────────────────────────────────────────────
  startWebcam = async () => {
    if (!this.videoRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        this.videoRef.current.play();
      }
    } catch (err) {
      // silent
    }
  };

  handleTakePhotoClick = () => {
    this.setState(
      { showVideo: true, capturedImageURL: null, files: [], newFiles: [] },
      () => {
        this.startWebcam();
      }
    );
  };

  captureImage = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const width = video.videoWidth;
      const height = video.videoHeight;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);
      const imageURL = canvas.toDataURL('image/jpeg', 0.5);
      this.setState({ capturedImageURL: imageURL }, () => {
        this.stopCamera();
      });
      this.handleFileAdd(imageURL);
    }
  };

  stopCamera = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ showVideo: false });
  };

  handleFileAdd = async (fileDataURL) => {
    fetch(fileDataURL)
      .then((res) => res.blob())
      .then(async (blob) => {
        const file = new File(
          [blob],
          `${Date.now() + Math.floor(Math.random() * 10000)}.jpg`,
          { type: 'image/jpeg' }
        );
        const newFile = await this.getFormattedFileObject(file);

        this.setState((prevState) => {
          prevState.files.forEach((oldFile) => {
            if (oldFile?.isBlob && oldFile.localURL) {
              URL.revokeObjectURL(oldFile.localURL);
            }
          });
          return {
            files: [newFile],
            newFiles: [newFile],
            capturedImageURL: newFile.localURL,
          };
        });
      });
  };

  getFormattedFileObject = async (file) => {
    return {
      localURL: URL.createObjectURL(file),
      displayName: file.name,
      type: file.type,
      file,
    };
  };

  startCameraMobile = async () => {
    try {
      const constraints = {
        video: { facingMode: { exact: 'environment' } },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.setState({ isCameraOpen: true });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }
    } catch (err) {
      // silent
    }
  };

  stopCameraMobile = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ isCameraOpen: false });
  };

  capturePhotoMobile = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const size = Math.min(video.videoWidth, video.videoHeight);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        video,
        (video.videoWidth - size) / 2,
        (video.videoHeight - size) / 2,
        size,
        size,
        0,
        0,
        size,
        size
      );
      const imageURL = canvas.toDataURL('image/jpeg', 0.8);
      this.setState({ capturedImageURL: imageURL }, () => {
        this.stopCameraMobile();
      });
      this.handleFileAdd(imageURL);
    }
  };

  handleOpenCameraMobile = () => {
    if (!this.state.isCameraOpen) {
      this.startCameraMobile();
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  isSaveDisabled = () => {
    const { firstName, lastName, phone, saving } = this.state;
    if (saving) return true;
    if (!firstName || !lastName) return true;
    if (!phone || phone.length !== 10) return true;
    return false;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  handleSave = () => {
    if (this.isSaveDisabled()) return;

    const {
      firstName,
      lastName,
      phone,
      dob,
      comments,
      files,
      newFiles,
      capturedImageURL,
    } = this.state;

    const customer = {
      locationId: this.props.user.selectedLocationId,
      customerId: '',
      firstName,
      lastName,
      dob,
      points: DEFAULT_POINTS,
      driverLicense: '',
      phone,
      address: '',
      comments,
      files,
      newFiles,
      isRule: false,
      maxMatchesPerDay: null,
      timeLimit: null,
      maxAmountPerDay: null,
      isBlocked: false,
      matchValues: [],
      userId: this.props.user.userId,
      employeeName: [this.props.user?.firstname, this.props.user?.lastname]
        .filter((p) => p && p !== 'null' && p !== 'undefined')
        .join(' '),
      capturedImageURL,
      walletType: 'apple',
    };

    this.setState({ saving: true });

    this.props.saveCustomer(
      customer,
      (data) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('success', 'Saved');
        if (this.props.onCustomerSaved) {
          this.props.onCustomerSaved(data?.customer || null);
        } else {
          this.props.handleEditingClear();
        }
      },
      (message) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('error', message);
      }
    );
  };

  renderPhotoDesktop = () => {
    return (
      <div className="customer-photo-section">
        {this.state.showVideo ? (
          <video
            ref={this.videoRef}
            className="customer-photo-video"
          />
        ) : (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        <div className="customer-photo-actions">
          {!this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleTakePhotoClick}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          )}
          {this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              startIcon={<Camera />}
              onClick={this.captureImage}
            >
              Capture Image
            </Button>
          )}
        </div>
      </div>
    );
  };

  renderPhotoMobile = () => {
    return (
      <div className="customer-photo-section">
        {!this.state.isCameraOpen && (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        {!this.state.isCameraOpen && (
          <div className="customer-photo-actions">
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleOpenCameraMobile}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          </div>
        )}

        {this.state.isCameraOpen && (
          <div className="video-container">
            <video
              ref={this.videoRef}
              className="responsive-video"
              autoPlay
              playsInline
              muted
            />
            <button
              style={styles.captureButton}
              onClick={this.capturePhotoMobile}
            >
              <div style={styles.captureIcon}></div>
            </button>
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <TopToolbar
          pageName={ADD_CUSTOMER_TOOLTIP}
          menuItems={[
            {
              title: BACK,
              icon: <KeyboardBackspaceIcon />,
              onClick: () => this.props.handleEditingClear(),
            },
          ]}
        />

        <div className="customer-add-wrapper">
          {this.state.isMobile
            ? this.renderPhotoMobile()
            : this.renderPhotoDesktop()}

          {!this.state.isCameraOpen && (
            <div className="customer-add-fields">
              {this.state.blacklistMatches.length > 0 &&
                !this.state.blacklistDismissed && (
                  <BlacklistWarningPanel
                    matches={this.state.blacklistMatches}
                    firstName={this.state.firstName}
                    onDismiss={() =>
                      this.setState({ blacklistDismissed: true })
                    }
                  />
                )}

              {this.state.duplicateNameMatches.length > 0 &&
                !this.state.duplicateNameDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicateNameMatches}
                    searchLabel={`${this.state.firstName.trim()} ${this.state.lastName.trim()}`}
                    matchType="name"
                    onDismiss={() =>
                      this.setState({ duplicateNameDismissed: true })
                    }
                  />
                )}

              {this.state.duplicatePhoneMatches.length > 0 &&
                !this.state.duplicatePhoneDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicatePhoneMatches}
                    searchLabel={this.state.phone}
                    matchType="phone"
                    onDismiss={() =>
                      this.setState({ duplicatePhoneDismissed: true })
                    }
                  />
                )}

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="First Name"
                InputLabelProps={{ shrink: true }}
                name="firstName"
                value={this.state.firstName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Last Name"
                InputLabelProps={{ shrink: true }}
                name="lastName"
                value={this.state.lastName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Phone"
                InputLabelProps={{ shrink: true }}
                name="phone"
                value={this.state.phone}
                onChange={this.handlePhoneChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  name="dob"
                  variant="inline"
                  inputVariant="outlined"
                  label="Date Of Birth"
                  className="customer-add-input"
                  format="MM/dd/yyyy"
                  placeholder="MM/DD/YYYY"
                  value={this.state.dob}
                  InputAdornmentProps={{ position: 'start' }}
                  onChange={(dateInput) => {
                    const date = new Date(dateInput?.toString());
                    if (date.toString() === 'Invalid Date') return;
                    this.handleDateChange(date, 'dob');
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Comments"
                InputLabelProps={{ shrink: true }}
                name="comments"
                value={this.state.comments}
                onChange={this.handleChange}
                multiline
                minRows={4}
                maxRows={6}
                fullWidth
              />

              <div className="customer-add-info-banner">
                <InfoOutlinedIcon className="customer-add-info-icon" />
                <span>
                  Points, Match Rules, and Wallet Pass can be configured from
                  the customer profile after saving.
                </span>
              </div>

              <Button
                className="customer-primary-btn"
                onClick={this.handleSave}
                disabled={this.isSaveDisabled()}
                fullWidth
              >
                {this.state.saving ? 'Saving...' : 'Save Customer'}
              </Button>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const styles = {
  captureButton: {
    position: 'fixed',
    bottom: '120px',
    left: '50%',
    transform: 'translateX(-50%)',
    border: 'none',
    borderRadius: '50%',
    width: '70px',
    height: '70px',
    backgroundColor: '#fff',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
  },
  captureIcon: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#333',
  },
};

export default CustomerForm;
